export type SpreadsheetWorkbookCellValue = string | number | boolean | null;

export type SpreadsheetWorkbookFormulaValue = `=${string}`;

export type SpreadsheetWorkbookCellEditableValue =
  | SpreadsheetWorkbookCellValue
  | SpreadsheetWorkbookFormulaValue;

export type SpreadsheetWorkbookCellFormatLike = {
  backgroundColor?: string;
  textColor?: string;
  bold?: boolean;
};

export type SpreadsheetWorkbookColumnLike = {
  key: string;
  header: string;
  kind: string;
  width?: number;
};

export type SpreadsheetWorkbookSummaryRowLike = {
  label: string;
  values?: Record<string, SpreadsheetWorkbookCellValue>;
};

export type SpreadsheetWorkbookSheetLike = {
  id: string;
  name: string;
  description?: string;
  tabColor?: string;
  columns: SpreadsheetWorkbookColumnLike[];
  rows: Array<Record<string, SpreadsheetWorkbookCellEditableValue>>;
  summaryRows?: SpreadsheetWorkbookSummaryRowLike[];
  cellFormats?: Record<string, SpreadsheetWorkbookCellFormatLike>;
};

export type SpreadsheetWorkbookVersionMetadataLike = {
  workspaceRootId?: string;
  versionId?: string;
  versionNumber?: number;
  previousVersionId?: string | null;
  manifestPath?: string | null;
  workbookArtifactPath?: string | null;
  xlsxArtifactPath?: string | null;
  csvArtifactPath?: string | null;
  updatedBy?: 'assistant' | 'user';
  updatedAt?: string;
};

export type SpreadsheetWorkbookLike = {
  version: 1;
  title: string;
  description?: string;
  generatedAt?: string;
  sourceQuestion?: string;
  appliedOperations?: string[];
  metadata?: SpreadsheetWorkbookVersionMetadataLike;
  sheets: SpreadsheetWorkbookSheetLike[];
};

export type SpreadsheetWorkbookModificationOperation =
  | {
      type: 'keep_only_columns';
      columnKeys: string[];
      columnHeaders: string[];
      description: string;
    }
  | {
      type: 'filter_rows';
      columnKey: string;
      columnHeader: string;
      value: SpreadsheetWorkbookCellValue;
      description: string;
    };

export type SpreadsheetWorkbookModificationPlan = {
  operations: SpreadsheetWorkbookModificationOperation[];
  summary: string;
  targetSheetId?: string;
  targetSheetName?: string;
};

export type SpreadsheetWorkbookSortDirection = 'asc' | 'desc';

const MODIFICATION_VERB_PATTERN = /\b(remove|hide|drop|delete|exclude|keep only|show only|only show|only include|apply filter|filter|limit to)\b/i;
const WORKBOOK_CONTEXT_PATTERN = /\b(spreadsheet|sheet|workbook|column|columns|row|rows|filter|status|grade|assignee|family|application|applicant|priority|interview|submitted|scheduled|accepted|applied|waitlisted|enrolled|denied|in progress)\b/i;
const FILTER_COMMAND_PATTERN = /\b(filter|apply filter|show only|only show|only include|keep only|limit to)\b/i;
const COLUMN_KEEP_PATTERN = /\b(keep only|only show|only include)\b[\s\S]{0,48}\bcolumns?\b/i;
const COLUMN_REMOVE_PATTERN = /\b(remove|hide|drop|delete|exclude)\b[\s\S]*\bcolumns?\b/i;
const SUMMARY_ROW_KEYS = ['count', 'priorityCount', 'standardCount'] as const;
const IGNORE_FILTER_VALUE_PATTERN = /^(all|applications?|applicants?|rows?|columns?|column|sheet|spreadsheet|workbook|current|only|show|filter|keep|remove|hide)$/i;

const normalizeText = (value: unknown): string => (
  String(value ?? '')
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
);

const cloneWorkbook = <T>(value: T): T => {
  if (typeof structuredClone === 'function') {
    return structuredClone(value);
  }
  return JSON.parse(JSON.stringify(value)) as T;
};

const toNumericValue = (value: SpreadsheetWorkbookCellEditableValue): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'boolean') {
    return value ? 1 : 0;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed || trimmed.startsWith('=')) {
      return null;
    }
    const parsed = Number(trimmed.replace(/,/g, ''));
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const normalizeColumnKey = (value: string, fallbackIndex: number): string => {
  const tokens = value
    .trim()
    .replace(/[^a-zA-Z0-9]+/g, ' ')
    .split(/\s+/)
    .map((token) => token.trim())
    .filter(Boolean)
    .map((token) => token.toLowerCase());
  if (tokens.length === 0) {
    return `column_${fallbackIndex + 1}`;
  }
  const [firstToken, ...remainingTokens] = tokens;
  return `${firstToken}${remainingTokens.map((token) => token.charAt(0).toUpperCase() + token.slice(1)).join('')}`;
};

const makeCellFormatKey = (rowIndex: number, columnKey: string): string => `${rowIndex}:${columnKey}`;

const titleCase = (value: string): string => (
  value
    .split(/\s+/)
    .filter(Boolean)
    .map((token) => token.charAt(0).toUpperCase() + token.slice(1))
    .join(' ')
);

const stripPreviousAssistantOperationSuffix = (value: string | undefined): string | undefined => {
  if (!value) {
    return undefined;
  }
  return value.replace(/\s*Applied by assistant:.*$/i, '').trim() || undefined;
};

const isFormulaValue = (value: SpreadsheetWorkbookCellEditableValue): value is SpreadsheetWorkbookFormulaValue => (
  typeof value === 'string' && value.trim().startsWith('=')
);

const parseFormulaArguments = (value: string): string[] => {
  const values: string[] = [];
  let current = '';
  let depth = 0;
  for (const char of value) {
    if (char === ',' && depth === 0) {
      if (current.trim()) {
        values.push(current.trim());
      }
      current = '';
      continue;
    }
    if (char === '(') depth += 1;
    if (char === ')') depth = Math.max(0, depth - 1);
    current += char;
  }
  if (current.trim()) {
    values.push(current.trim());
  }
  return values;
};

const safeEvaluateArithmeticExpression = (expression: string): number | null => {
  if (!/^[0-9+\-*/().\s]+$/.test(expression)) {
    return null;
  }
  try {
    const fn = new Function(`return (${expression});`);
    const result = fn();
    return typeof result === 'number' && Number.isFinite(result) ? result : null;
  } catch {
    return null;
  }
};

export const evaluateSpreadsheetFormulaValue = (input: {
  formula: SpreadsheetWorkbookFormulaValue;
  row: Record<string, SpreadsheetWorkbookCellEditableValue>;
}): SpreadsheetWorkbookCellEditableValue => {
  const raw = input.formula.slice(1).trim();
  const upper = raw.toUpperCase();

  const builtinMatch = upper.match(/^(SUM|AVG|MIN|MAX|COUNT)\((.*)\)$/);
  if (builtinMatch) {
    const fnName = builtinMatch[1]!;
    const args = parseFormulaArguments(raw.slice(fnName.length + 1, -1));
    const numericArgs = args
      .map((token) => {
        const normalizedToken = token.trim();
        const rowValue = input.row[normalizedToken];
        return toNumericValue(rowValue ?? normalizedToken);
      })
      .filter((value): value is number => value != null);

    if (fnName === 'COUNT') {
      return numericArgs.length;
    }
    if (numericArgs.length === 0) {
      return null;
    }
    if (fnName === 'SUM') {
      return numericArgs.reduce((sum, value) => sum + value, 0);
    }
    if (fnName === 'AVG') {
      return numericArgs.reduce((sum, value) => sum + value, 0) / numericArgs.length;
    }
    if (fnName === 'MIN') {
      return Math.min(...numericArgs);
    }
    if (fnName === 'MAX') {
      return Math.max(...numericArgs);
    }
  }

  const tokens = raw.replace(/[A-Za-z_][A-Za-z0-9_]*/g, (token) => {
    const numericValue = toNumericValue(input.row[token]);
    return numericValue == null ? '0' : String(numericValue);
  });
  const evaluated = safeEvaluateArithmeticExpression(tokens);
  return evaluated == null ? input.formula : evaluated;
};

export const coerceSpreadsheetCellValue = (
  raw: unknown,
  fallback: SpreadsheetWorkbookCellEditableValue = null,
): SpreadsheetWorkbookCellEditableValue => {
  if (raw == null) {
    return fallback;
  }
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    return trimmed.length > 0 ? trimmed : fallback;
  }
  if (typeof raw === 'number' || typeof raw === 'boolean') {
    return raw;
  }
  return String(raw);
};

const normalizeSummaryRows = (
  summaryRows: SpreadsheetWorkbookSummaryRowLike[] | undefined,
): SpreadsheetWorkbookSummaryRowLike[] | undefined => {
  if (!summaryRows?.length) {
    return undefined;
  }
  return summaryRows.map((summaryRow) => ({
    ...summaryRow,
    label: titleCase(summaryRow.label),
    values: Object.fromEntries(
      Object.entries(summaryRow.values ?? {}).filter(([key]) => SUMMARY_ROW_KEYS.includes(key as typeof SUMMARY_ROW_KEYS[number])),
    ),
  }));
};

export const rebuildSpreadsheetWorkbookDerivedSheets = (workbook: SpreadsheetWorkbookLike): SpreadsheetWorkbookLike => {
  const applicantsSheet = resolveWorkbookTargetSheet({ workbook });
  if (!applicantsSheet) {
    return workbook;
  }

  const applicantRows = applicantsSheet.rows.slice();
  const nextSheets = workbook.sheets.map((sheet) => {
    const normalizedSheetName = normalizeText(sheet.name);
    const normalizedSheetId = normalizeText(sheet.id);
    if (normalizedSheetId === 'status summary' || normalizedSheetName === 'status summary') {
      return rebuildSummarySheet(sheet, applicantRows, 'status', 'Unknown status');
    }
    if (normalizedSheetId === 'grade summary' || normalizedSheetName === 'grade summary') {
      return rebuildSummarySheet(sheet, applicantRows, 'grade', 'Unknown grade');
    }
    if (normalizedSheetId === 'assignee summary' || normalizedSheetName === 'assignee summary') {
      return rebuildSummarySheet(sheet, applicantRows, 'assignee', 'Unassigned');
    }
    return {
      ...sheet,
      summaryRows: normalizeSummaryRows(sheet.summaryRows),
    };
  });

  return {
    ...workbook,
    generatedAt: new Date().toISOString(),
    sheets: nextSheets,
  };
};

const buildBlankRow = (
  columns: SpreadsheetWorkbookColumnLike[],
): Record<string, SpreadsheetWorkbookCellEditableValue> => (
  Object.fromEntries(columns.map((column) => [column.key, null]))
);

export const updateSpreadsheetWorkbookCell = (input: {
  workbook: SpreadsheetWorkbookLike;
  sheetId: string;
  rowIndex: number;
  columnKey: string;
  value: SpreadsheetWorkbookCellEditableValue;
}): SpreadsheetWorkbookLike => {
  const nextWorkbook = cloneWorkbook(input.workbook);
  const sheet = nextWorkbook.sheets.find((candidate) => candidate.id === input.sheetId);
  if (!sheet) {
    throw new Error('The selected sheet was not found.');
  }
  if (!sheet.rows[input.rowIndex]) {
    throw new Error('The selected row was not found.');
  }
  sheet.rows[input.rowIndex] = {
    ...sheet.rows[input.rowIndex],
    [input.columnKey]: input.value,
  };
  return rebuildSpreadsheetWorkbookDerivedSheets(nextWorkbook);
};

export const addSpreadsheetWorkbookRow = (input: {
  workbook: SpreadsheetWorkbookLike;
  sheetId: string;
  index?: number;
}): SpreadsheetWorkbookLike => {
  const nextWorkbook = cloneWorkbook(input.workbook);
  const sheet = nextWorkbook.sheets.find((candidate) => candidate.id === input.sheetId);
  if (!sheet) {
    throw new Error('The selected sheet was not found.');
  }
  const insertIndex = typeof input.index === 'number'
    ? Math.max(0, Math.min(input.index, sheet.rows.length))
    : sheet.rows.length;
  sheet.rows.splice(insertIndex, 0, buildBlankRow(sheet.columns));
  return rebuildSpreadsheetWorkbookDerivedSheets(nextWorkbook);
};

export const duplicateSpreadsheetWorkbookRow = (input: {
  workbook: SpreadsheetWorkbookLike;
  sheetId: string;
  rowIndex: number;
}): SpreadsheetWorkbookLike => {
  const nextWorkbook = cloneWorkbook(input.workbook);
  const sheet = nextWorkbook.sheets.find((candidate) => candidate.id === input.sheetId);
  if (!sheet?.rows[input.rowIndex]) {
    throw new Error('The selected row was not found.');
  }
  sheet.rows.splice(input.rowIndex + 1, 0, cloneWorkbook(sheet.rows[input.rowIndex]!));
  return rebuildSpreadsheetWorkbookDerivedSheets(nextWorkbook);
};

export const deleteSpreadsheetWorkbookRow = (input: {
  workbook: SpreadsheetWorkbookLike;
  sheetId: string;
  rowIndex: number;
}): SpreadsheetWorkbookLike => {
  const nextWorkbook = cloneWorkbook(input.workbook);
  const sheet = nextWorkbook.sheets.find((candidate) => candidate.id === input.sheetId);
  if (!sheet?.rows[input.rowIndex]) {
    throw new Error('The selected row was not found.');
  }
  sheet.rows.splice(input.rowIndex, 1);
  return rebuildSpreadsheetWorkbookDerivedSheets(nextWorkbook);
};

export const addSpreadsheetWorkbookColumn = (input: {
  workbook: SpreadsheetWorkbookLike;
  sheetId: string;
  header: string;
  kind?: string;
  index?: number;
}): SpreadsheetWorkbookLike => {
  const nextWorkbook = cloneWorkbook(input.workbook);
  const sheet = nextWorkbook.sheets.find((candidate) => candidate.id === input.sheetId);
  if (!sheet) {
    throw new Error('The selected sheet was not found.');
  }
  const header = input.header.trim() || 'New Column';
  const baseKey = normalizeColumnKey(header, sheet.columns.length);
  const existingKeys = new Set(sheet.columns.map((column) => column.key));
  let key = baseKey;
  let suffix = 2;
  while (existingKeys.has(key)) {
    key = `${baseKey}_${suffix}`;
    suffix += 1;
  }
  const insertIndex = typeof input.index === 'number'
    ? Math.max(0, Math.min(input.index, sheet.columns.length))
    : sheet.columns.length;
  sheet.columns.splice(insertIndex, 0, {
    key,
    header,
    kind: input.kind ?? 'text',
    width: 18,
  });
  sheet.rows = sheet.rows.map((row) => ({
    ...row,
    [key]: null,
  }));
  return rebuildSpreadsheetWorkbookDerivedSheets(nextWorkbook);
};

export const updateSpreadsheetWorkbookColumn = (input: {
  workbook: SpreadsheetWorkbookLike;
  sheetId: string;
  columnKey: string;
  header?: string;
  kind?: string;
  width?: number;
}): SpreadsheetWorkbookLike => {
  const nextWorkbook = cloneWorkbook(input.workbook);
  const sheet = nextWorkbook.sheets.find((candidate) => candidate.id === input.sheetId);
  const column = sheet?.columns.find((candidate) => candidate.key === input.columnKey);
  if (!sheet || !column) {
    throw new Error('The selected column was not found.');
  }
  if (input.header != null) {
    column.header = input.header.trim() || column.header;
  }
  if (input.kind != null) {
    column.kind = input.kind;
  }
  if (typeof input.width === 'number' && Number.isFinite(input.width)) {
    column.width = Math.min(Math.max(input.width, 8), 42);
  }
  return rebuildSpreadsheetWorkbookDerivedSheets(nextWorkbook);
};

export const deleteSpreadsheetWorkbookColumn = (input: {
  workbook: SpreadsheetWorkbookLike;
  sheetId: string;
  columnKey: string;
}): SpreadsheetWorkbookLike => {
  const nextWorkbook = cloneWorkbook(input.workbook);
  const sheet = nextWorkbook.sheets.find((candidate) => candidate.id === input.sheetId);
  if (!sheet) {
    throw new Error('The selected sheet was not found.');
  }
  sheet.columns = sheet.columns.filter((column) => column.key !== input.columnKey);
  sheet.rows = sheet.rows.map((row) => {
    const nextRow = { ...row };
    delete nextRow[input.columnKey];
    return nextRow;
  });
  if (sheet.cellFormats) {
    sheet.cellFormats = Object.fromEntries(
      Object.entries(sheet.cellFormats).filter(([, _format], index) => {
        const key = Object.keys(sheet.cellFormats ?? {})[index];
        return !key.endsWith(`:${input.columnKey}`);
      }),
    );
  }
  return rebuildSpreadsheetWorkbookDerivedSheets(nextWorkbook);
};

export const reorderSpreadsheetWorkbookColumns = (input: {
  workbook: SpreadsheetWorkbookLike;
  sheetId: string;
  fromIndex: number;
  toIndex: number;
}): SpreadsheetWorkbookLike => {
  const nextWorkbook = cloneWorkbook(input.workbook);
  const sheet = nextWorkbook.sheets.find((candidate) => candidate.id === input.sheetId);
  if (!sheet) {
    throw new Error('The selected sheet was not found.');
  }
  if (input.fromIndex < 0 || input.fromIndex >= sheet.columns.length || input.toIndex < 0 || input.toIndex >= sheet.columns.length) {
    return nextWorkbook;
  }
  const [moved] = sheet.columns.splice(input.fromIndex, 1);
  if (moved) {
    sheet.columns.splice(input.toIndex, 0, moved);
  }
  return rebuildSpreadsheetWorkbookDerivedSheets(nextWorkbook);
};

export const addSpreadsheetWorkbookSheet = (input: {
  workbook: SpreadsheetWorkbookLike;
  name?: string;
}): SpreadsheetWorkbookLike => {
  const nextWorkbook = cloneWorkbook(input.workbook);
  const baseName = (input.name?.trim() || 'Sheet').slice(0, 31);
  const existingNames = new Set(nextWorkbook.sheets.map((sheet) => sheet.name.toLowerCase()));
  let name = baseName;
  let suffix = 2;
  while (existingNames.has(name.toLowerCase())) {
    name = `${baseName.slice(0, Math.max(1, 31 - String(suffix).length - 1))} ${suffix}`;
    suffix += 1;
  }
  nextWorkbook.sheets.push({
    id: normalizeColumnKey(name, nextWorkbook.sheets.length),
    name,
    columns: [
      { key: 'column_1', header: 'Column 1', kind: 'text', width: 18 },
      { key: 'column_2', header: 'Column 2', kind: 'text', width: 18 },
    ],
    rows: [],
  });
  return rebuildSpreadsheetWorkbookDerivedSheets(nextWorkbook);
};

export const updateSpreadsheetWorkbookSheet = (input: {
  workbook: SpreadsheetWorkbookLike;
  sheetId: string;
  name?: string;
  description?: string;
  tabColor?: string | null;
}): SpreadsheetWorkbookLike => {
  const nextWorkbook = cloneWorkbook(input.workbook);
  const sheet = nextWorkbook.sheets.find((candidate) => candidate.id === input.sheetId);
  if (!sheet) {
    throw new Error('The selected sheet was not found.');
  }
  if (input.name != null) {
    sheet.name = input.name.trim() || sheet.name;
  }
  if (input.description != null) {
    sheet.description = input.description.trim() || undefined;
  }
  if (input.tabColor !== undefined) {
    sheet.tabColor = input.tabColor || undefined;
  }
  return rebuildSpreadsheetWorkbookDerivedSheets(nextWorkbook);
};

export const deleteSpreadsheetWorkbookSheet = (input: {
  workbook: SpreadsheetWorkbookLike;
  sheetId: string;
}): SpreadsheetWorkbookLike => {
  const nextWorkbook = cloneWorkbook(input.workbook);
  if (nextWorkbook.sheets.length <= 1) {
    throw new Error('At least one sheet must remain in the workbook.');
  }
  nextWorkbook.sheets = nextWorkbook.sheets.filter((sheet) => sheet.id !== input.sheetId);
  return rebuildSpreadsheetWorkbookDerivedSheets(nextWorkbook);
};

export const reorderSpreadsheetWorkbookSheets = (input: {
  workbook: SpreadsheetWorkbookLike;
  fromIndex: number;
  toIndex: number;
}): SpreadsheetWorkbookLike => {
  const nextWorkbook = cloneWorkbook(input.workbook);
  if (
    input.fromIndex < 0
    || input.fromIndex >= nextWorkbook.sheets.length
    || input.toIndex < 0
    || input.toIndex >= nextWorkbook.sheets.length
  ) {
    return nextWorkbook;
  }
  const [moved] = nextWorkbook.sheets.splice(input.fromIndex, 1);
  if (moved) {
    nextWorkbook.sheets.splice(input.toIndex, 0, moved);
  }
  return rebuildSpreadsheetWorkbookDerivedSheets(nextWorkbook);
};

const compareValues = (
  left: SpreadsheetWorkbookCellEditableValue,
  right: SpreadsheetWorkbookCellEditableValue,
): number => {
  const leftNumber = toNumericValue(left);
  const rightNumber = toNumericValue(right);
  if (leftNumber != null && rightNumber != null) {
    return leftNumber - rightNumber;
  }
  return String(left ?? '').localeCompare(String(right ?? ''), undefined, { numeric: true, sensitivity: 'base' });
};

export const sortSpreadsheetWorkbookSheetRows = (input: {
  workbook: SpreadsheetWorkbookLike;
  sheetId: string;
  columnKey: string;
  direction: SpreadsheetWorkbookSortDirection;
}): SpreadsheetWorkbookLike => {
  const nextWorkbook = cloneWorkbook(input.workbook);
  const sheet = nextWorkbook.sheets.find((candidate) => candidate.id === input.sheetId);
  if (!sheet) {
    throw new Error('The selected sheet was not found.');
  }
  sheet.rows.sort((left, right) => {
    const comparison = compareValues(left[input.columnKey] ?? null, right[input.columnKey] ?? null);
    return input.direction === 'asc' ? comparison : comparison * -1;
  });
  return rebuildSpreadsheetWorkbookDerivedSheets(nextWorkbook);
};

export const filterSpreadsheetWorkbookSheetRows = (input: {
  workbook: SpreadsheetWorkbookLike;
  sheetId: string;
  columnKey: string;
  value: SpreadsheetWorkbookCellEditableValue;
}): SpreadsheetWorkbookLike => {
  const nextWorkbook = cloneWorkbook(input.workbook);
  const sheet = nextWorkbook.sheets.find((candidate) => candidate.id === input.sheetId);
  if (!sheet) {
    throw new Error('The selected sheet was not found.');
  }
  sheet.rows = sheet.rows.filter((row) => compareValues(row[input.columnKey] ?? null, input.value) === 0);
  return rebuildSpreadsheetWorkbookDerivedSheets(nextWorkbook);
};

export const setSpreadsheetWorkbookCellFormat = (input: {
  workbook: SpreadsheetWorkbookLike;
  sheetId: string;
  rowIndex: number;
  columnKey: string;
  format: SpreadsheetWorkbookCellFormatLike;
}): SpreadsheetWorkbookLike => {
  const nextWorkbook = cloneWorkbook(input.workbook);
  const sheet = nextWorkbook.sheets.find((candidate) => candidate.id === input.sheetId);
  if (!sheet) {
    throw new Error('The selected sheet was not found.');
  }
  sheet.cellFormats = {
    ...(sheet.cellFormats ?? {}),
    [makeCellFormatKey(input.rowIndex, input.columnKey)]: input.format,
  };
  return nextWorkbook;
};

export const getSpreadsheetWorkbookCellFormat = (input: {
  sheet: SpreadsheetWorkbookSheetLike;
  rowIndex: number;
  columnKey: string;
}): SpreadsheetWorkbookCellFormatLike | null => (
  input.sheet.cellFormats?.[makeCellFormatKey(input.rowIndex, input.columnKey)] ?? null
);

const resolveWorkbookTargetSheet = (input: {
  workbook: SpreadsheetWorkbookLike;
  preferredSheetId?: string | null;
  preferredSheetName?: string | null;
}): SpreadsheetWorkbookSheetLike | null => (
  (input.preferredSheetId
    ? input.workbook.sheets.find((sheet) => sheet.id === input.preferredSheetId)
    : null)
  ?? (input.preferredSheetName
    ? input.workbook.sheets.find((sheet) => normalizeText(sheet.name) === normalizeText(input.preferredSheetName))
    : null)
  ?? input.workbook.sheets.find((sheet) => normalizeText(sheet.id) === 'applicants')
  ?? input.workbook.sheets.find((sheet) => normalizeText(sheet.name) === 'applicants')
  ?? input.workbook.sheets[0]
  ?? null
);

const columnAliasesFor = (column: SpreadsheetWorkbookColumnLike): string[] => {
  const baseAliases = new Set<string>([
    normalizeText(column.key),
    normalizeText(column.header),
  ]);

  switch (column.key) {
    case 'applicationCode':
      baseAliases.add('application');
      baseAliases.add('application code');
      baseAliases.add('app code');
      break;
    case 'name':
      baseAliases.add('applicant');
      baseAliases.add('student');
      break;
    case 'familyName':
      baseAliases.add('family');
      baseAliases.add('family name');
      break;
    case 'submittedAt':
      baseAliases.add('submitted');
      baseAliases.add('submission date');
      break;
    case 'interviewDate':
      baseAliases.add('interview');
      baseAliases.add('interview date');
      break;
    case 'studentsCount':
      baseAliases.add('students');
      baseAliases.add('student count');
      break;
    default:
      break;
  }

  return [...baseAliases].filter(Boolean);
};

const questionMentionsAlias = (normalizedQuestion: string, alias: string): boolean => {
  if (!alias) {
    return false;
  }
  const safeAlias = alias.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`(^|\\s)${safeAlias}(?=\\s|$)`, 'i').test(normalizedQuestion);
};

const findMentionedColumns = (
  normalizedQuestion: string,
  sheet: SpreadsheetWorkbookSheetLike,
): SpreadsheetWorkbookColumnLike[] => (
  sheet.columns.filter((column) => (
    columnAliasesFor(column).some((alias) => questionMentionsAlias(normalizedQuestion, alias))
  ))
);

const extractColumnSelectionOperation = (
  normalizedQuestion: string,
  sheet: SpreadsheetWorkbookSheetLike,
): SpreadsheetWorkbookModificationOperation | null => {
  const mentionedColumns = findMentionedColumns(normalizedQuestion, sheet);
  if (mentionedColumns.length === 0) {
    return null;
  }

  if (COLUMN_KEEP_PATTERN.test(normalizedQuestion)) {
    return {
      type: 'keep_only_columns',
      columnKeys: mentionedColumns.map((column) => column.key),
      columnHeaders: mentionedColumns.map((column) => column.header),
      description: `Kept only ${mentionedColumns.map((column) => column.header).join(', ')}`,
    };
  }

  if (!COLUMN_REMOVE_PATTERN.test(normalizedQuestion)) {
    return null;
  }

  const retainedColumns = sheet.columns.filter((column) => !mentionedColumns.some((entry) => entry.key === column.key));
  return {
    type: 'keep_only_columns',
    columnKeys: retainedColumns.map((column) => column.key),
    columnHeaders: retainedColumns.map((column) => column.header),
    description: `Removed ${mentionedColumns.map((column) => column.header).join(', ')}`,
  };
};

const getDistinctCellValues = (
  sheet: SpreadsheetWorkbookSheetLike,
  column: SpreadsheetWorkbookColumnLike,
): SpreadsheetWorkbookCellValue[] => {
  const seen = new Set<string>();
  const values: SpreadsheetWorkbookCellValue[] = [];
  for (const row of sheet.rows) {
    const value = row[column.key];
    const normalized = normalizeText(value);
    if (!normalized || seen.has(normalized)) {
      continue;
    }
    seen.add(normalized);
    values.push(value ?? null);
  }
  return values;
};

const resolveExplicitFilterColumn = (
  normalizedQuestion: string,
  sheet: SpreadsheetWorkbookSheetLike,
): SpreadsheetWorkbookColumnLike | null => (
  sheet.columns.find((column) => (
    columnAliasesFor(column).some((alias) => questionMentionsAlias(normalizedQuestion, alias))
  )) ?? null
);

const isPriorityFilterRequest = (normalizedQuestion: string): boolean => (
  /\bpriority\b/.test(normalizedQuestion)
  && !/\bremove\b[\s\S]*\bpriority\b/.test(normalizedQuestion)
);

const extractFilterOperation = (
  normalizedQuestion: string,
  sheet: SpreadsheetWorkbookSheetLike,
): SpreadsheetWorkbookModificationOperation | null => {
  if (!FILTER_COMMAND_PATTERN.test(normalizedQuestion) && !isPriorityFilterRequest(normalizedQuestion)) {
    return null;
  }

  if (isPriorityFilterRequest(normalizedQuestion)) {
    const priorityColumn = sheet.columns.find((column) => column.key === 'priority' || normalizeText(column.header) === 'priority');
    if (priorityColumn) {
      return {
        type: 'filter_rows',
        columnKey: priorityColumn.key,
        columnHeader: priorityColumn.header,
        value: 'Yes',
        description: `Filtered ${priorityColumn.header} to Yes`,
      };
    }
  }

  const explicitColumn = resolveExplicitFilterColumn(normalizedQuestion, sheet);
  let bestMatch: {
    column: SpreadsheetWorkbookColumnLike;
    value: SpreadsheetWorkbookCellValue;
    score: number;
  } | null = null;

  for (const column of sheet.columns) {
    for (const value of getDistinctCellValues(sheet, column)) {
      const normalizedValue = normalizeText(value);
      if (
        normalizedValue.length < 2
        || IGNORE_FILTER_VALUE_PATTERN.test(normalizedValue)
        || !normalizedQuestion.includes(normalizedValue)
      ) {
        continue;
      }
      const score = normalizedValue.length + (explicitColumn?.key === column.key ? 100 : 0);
      if (!bestMatch || score > bestMatch.score) {
        bestMatch = {
          column,
          value,
          score,
        };
      }
    }
  }

  if (!bestMatch) {
    return null;
  }

  return {
    type: 'filter_rows',
    columnKey: bestMatch.column.key,
    columnHeader: bestMatch.column.header,
    value: bestMatch.value,
    description: `Filtered ${bestMatch.column.header} to ${String(bestMatch.value)}`,
  };
};

const buildSummaryRows = (
  rows: Array<Record<string, SpreadsheetWorkbookCellValue>>,
  key: string,
  emptyLabel: string,
): SpreadsheetWorkbookSummaryRowLike[] => {
  const grouped = new Map<string, { label: string; count: number; priorityCount: number; standardCount: number }>();
  for (const row of rows) {
    const rawLabel = String(row[key] ?? '').trim() || emptyLabel;
    const entry = grouped.get(rawLabel) ?? {
      label: rawLabel,
      count: 0,
      priorityCount: 0,
      standardCount: 0,
    };
    entry.count += 1;
    if (String(row.priority ?? '').trim().toLowerCase() === 'yes') {
      entry.priorityCount += 1;
    } else {
      entry.standardCount += 1;
    }
    grouped.set(rawLabel, entry);
  }
  return [...grouped.values()].map((entry) => ({
    label: entry.label,
    values: {
      count: entry.count,
      priorityCount: entry.priorityCount,
      standardCount: entry.standardCount,
    },
  }));
};

const rebuildSummarySheet = (
  sheet: SpreadsheetWorkbookSheetLike,
  rows: Array<Record<string, SpreadsheetWorkbookCellValue>>,
  key: string,
  emptyLabel: string,
): SpreadsheetWorkbookSheetLike => {
  const summaryRows = buildSummaryRows(rows, key, emptyLabel);
  return {
    ...sheet,
    rows: summaryRows.map((entry) => ({
      label: entry.label,
      count: entry.values?.count ?? 0,
      priorityCount: entry.values?.priorityCount ?? 0,
      standardCount: entry.values?.standardCount ?? 0,
    })),
    summaryRows,
  };
};

const formatCellForComparison = (value: SpreadsheetWorkbookCellEditableValue): string => normalizeText(value);

const rowMatchesValue = (
  row: Record<string, SpreadsheetWorkbookCellValue>,
  operation: Extract<SpreadsheetWorkbookModificationOperation, { type: 'filter_rows' }>,
): boolean => (
  formatCellForComparison(row[operation.columnKey]) === normalizeText(operation.value)
);

export const isSpreadsheetModificationPrompt = (question: string): boolean => {
  const normalizedQuestion = normalizeText(question);
  return MODIFICATION_VERB_PATTERN.test(normalizedQuestion) && WORKBOOK_CONTEXT_PATTERN.test(normalizedQuestion);
};

export const buildSpreadsheetWorkbookModificationPlan = (input: {
  question: string;
  workbook: SpreadsheetWorkbookLike;
  preferredSheetId?: string | null;
  preferredSheetName?: string | null;
}): SpreadsheetWorkbookModificationPlan => {
  const applicantsSheet = resolveWorkbookTargetSheet({
    workbook: input.workbook,
    preferredSheetId: input.preferredSheetId,
    preferredSheetName: input.preferredSheetName,
  });
  if (!applicantsSheet) {
    throw new Error('The current spreadsheet does not expose an applicants sheet.');
  }

  const normalizedQuestion = normalizeText(input.question);
  const operations: SpreadsheetWorkbookModificationOperation[] = [];
  const columnOperation = extractColumnSelectionOperation(normalizedQuestion, applicantsSheet);
  if (columnOperation) {
    operations.push(columnOperation);
  }
  const filterOperation = extractFilterOperation(normalizedQuestion, applicantsSheet);
  if (filterOperation) {
    operations.push(filterOperation);
  }

  if (operations.length === 0) {
    throw new Error(
      'The assistant can currently modify this spreadsheet by restructuring columns or filtering rows using grounded values from the current workbook.',
    );
  }

  return {
    operations,
    summary: operations.map((operation) => operation.description).join('. '),
    targetSheetId: applicantsSheet.id,
    targetSheetName: applicantsSheet.name,
  };
};

export const applySpreadsheetWorkbookModificationPlan = (input: {
  workbook: SpreadsheetWorkbookLike;
  plan: SpreadsheetWorkbookModificationPlan;
  question: string;
}): SpreadsheetWorkbookLike => {
  const applicantsSheet = resolveWorkbookTargetSheet({
    workbook: input.workbook,
    preferredSheetId: input.plan.targetSheetId,
    preferredSheetName: input.plan.targetSheetName,
  });
  if (!applicantsSheet) {
    throw new Error('The current spreadsheet does not expose an applicants sheet.');
  }

  let nextRows = applicantsSheet.rows.slice();
  let visibleColumnKeys = applicantsSheet.columns.map((column) => column.key);

  for (const operation of input.plan.operations) {
    if (operation.type === 'keep_only_columns') {
      visibleColumnKeys = visibleColumnKeys.filter((columnKey) => operation.columnKeys.includes(columnKey));
      continue;
    }
    if (operation.type === 'filter_rows') {
      nextRows = nextRows.filter((row) => rowMatchesValue(row, operation));
    }
  }

  const nextSheets = input.workbook.sheets.map((sheet) => {
    if (sheet === applicantsSheet) {
      return {
        ...sheet,
        columns: sheet.columns.filter((column) => visibleColumnKeys.includes(column.key)),
        rows: nextRows,
      };
    }
    return sheet;
  });

  const baseDescription = stripPreviousAssistantOperationSuffix(input.workbook.description);
  const appliedOperations = input.plan.operations.map((operation) => operation.description);
  const description = [
    baseDescription,
    `Applied by assistant: ${input.plan.summary}.`,
  ].filter(Boolean).join(' ');

  return {
    ...input.workbook,
    description,
    generatedAt: new Date().toISOString(),
    sourceQuestion: input.question,
    appliedOperations,
    sheets: rebuildSpreadsheetWorkbookDerivedSheets({
      ...input.workbook,
      sheets: nextSheets,
    }).sheets,
  };
};
